import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Inbox,
  Calendar,
  CalendarDays,
  Folder,
  Settings,
  Plus,
  ChevronLeft,
  Users,
  History,
  ClipboardCheck,
  BarChart3,
  Repeat,
  Bell,
  X,
  UserCircle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { useOrganizationStore } from '../../stores/organizationStore';
import { useUIStore } from '../../stores/uiStore';
import { Button } from '../common';
import { NotificationDot } from '../ui/NotificationDot';
import { useSidebarBadges } from '../../contexts/NotificationStateContext';
import { usePageSeen } from '../../hooks';

// Navigation items - only 'inbox' and 'reminders' show notification dots per requirements
// These correspond to the server-side UserNotificationState page types
const navigation = [
  { name: 'Inbox', icon: Inbox, path: '/', showBadge: true },
  { name: 'Today', icon: Calendar, path: '/today', showBadge: false },
  { name: 'Upcoming', icon: CalendarDays, path: '/upcoming', showBadge: false },
  { name: 'Recurring', icon: Repeat, path: '/recurring', showBadge: false },
  { name: 'Reminders', icon: Bell, path: '/reminders', showBadge: true },
  { name: 'Projects', icon: Folder, path: '/projects', showBadge: false },
  { name: 'Task History', icon: History, path: '/history', showBadge: false },
  { name: 'Statistics', icon: BarChart3, path: '/statistics', adminOnly: true, showBadge: false },
  { name: 'Users', icon: Users, path: '/users', adminOnly: true, showBadge: false },
  { name: 'User Tasks', icon: UserCircle, path: '/user-tasks', adminOnly: true, showBadge: false },
  { name: 'Attendances', icon: ClipboardCheck, path: '/attendances', showBadge: false },
  { name: 'Settings', icon: Settings, path: '/settings', adminOnly: true, showBadge: false },
];

const navKeys: Record<string, string> = {
  Inbox: 'inbox',
  Today: 'today',
  Upcoming: 'upcoming',
  Recurring: 'recurring',
  Reminders: 'reminders',
  Projects: 'projects',
  'Task History': 'taskHistory',
  Statistics: 'statistics',
  Users: 'users',
  'User Tasks': 'userTasks',
  Attendances: 'attendances',
  Settings: 'settings',
};

export const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuthStore();
  const {
    sidebarOpen,
    toggleSidebar,
    openTaskModal,
    openProjectModal,
    mobileMenuOpen,
    setMobileMenuOpen,
    darkMode,
    toggleDarkMode,
  } = useUIStore();

  // Badge counts come from unified NotificationStateContext
  // Single API call updates both bell (Header) and sidebar badges
  const { badges } = useSidebarBadges();

  const { activeOrganizationId } = useOrganizationStore();

  // Automatically mark current page as seen when user navigates to it
  // usePageSeen handles server-side badge clearing
  usePageSeen({ hasLoadedContent: true });

  // Only show New Project button in Projects page
  const isProjectsPage = location.pathname === '/projects';
  // Show users page for admins in the current organization
  const activeOrgRole = user?.organizationRoles?.find(r => r.orgId === activeOrganizationId)?.role;
  const isAdmin = activeOrgRole === 'ADMIN';

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, setMobileMenuOpen]);

  // Close mobile menu on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen, setMobileMenuOpen]);

  // Handle task modal open from mobile menu
  const handleNewTask = () => {
    openTaskModal();
    setMobileMenuOpen(false);
  };

  // Handle project modal open from mobile menu
  const handleNewProject = () => {
    openProjectModal();
    setMobileMenuOpen(false);
  };

  // Get badge count for a nav item - only for items with showBadge: true
  // Badge counts come from unified NotificationStateContext
  const getBadgeCount = (navItemName: string, showBadge?: boolean): number => {
    if (!showBadge) return 0;

    const key = navItemName.toLowerCase() as keyof typeof badges;
    const count = badges[key] || 0;
    return count;
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile sidebar drawer */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800
          z-50 flex flex-col w-72 transform transition-transform duration-300 ease-in-out
          md:hidden pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pb-[env(safe-area-inset-bottom)]
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Mobile header with close button */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800">
          <span className="text-xl font-bold text-primary-600">TaskFly</span>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={t('common.closeMenu')}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Mobile Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {navigation
            .filter(item => !item.adminOnly || isAdmin)
            .map((item) => {
              const badgeCount = getBadgeCount(item.name, item.showBadge);
              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `relative flex items-center gap-3 px-3 py-3 rounded-lg transition-colors min-h-[48px] ${
                      isActive
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                    }`
                  }
                >
                  <div className="relative">
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <NotificationDot
                      visible={badgeCount > 0}
                      ariaLabel={t('layout.badge.newItems', { count: badgeCount, name: t(`layout.nav.${navKeys[item.name]}`) })}
                    />
                  </div>
                  <span className="font-medium">{t(`layout.nav.${navKeys[item.name]}`)}</span>
                  <span className={`ml-auto text-xs bg-red-500 text-white rounded-full px-2 py-0.5 min-w-[20px] text-center transition-all duration-300 ${badgeCount > 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'}`}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                </NavLink>
              );
            })}
        </nav>

        {/* Dark mode toggle - accessible on xs screens where header lacks it */}
        <button
          onClick={toggleDarkMode}
          className="sm:hidden flex items-center gap-3 px-3 py-3 mx-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors min-h-[48px]"
          aria-label={darkMode ? t('layout.switchToLightMode') : t('layout.switchToDarkMode')}
        >
          {darkMode ? (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
          <span className="font-medium">{darkMode ? t('layout.theme.lightMode') : t('layout.theme.darkMode')}</span>
        </button>

        {/* Mobile Quick actions */}
        <div className="px-2 py-2 border-t border-gray-200 dark:border-gray-800 space-y-2">
          <Button
            variant="primary"
            className="w-full justify-start min-h-[48px]"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={handleNewTask}
          >{t('common.newTask')}</Button>
          {isProjectsPage && (
            <Button
              variant="secondary"
              className="w-full justify-start min-h-[48px]"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={handleNewProject}
            >{t('common.newProject')}</Button>
          )}
        </div>
      </aside>

      {/* Desktop sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800
          transition-all duration-300 z-40 flex-col hidden md:flex
          pt-[env(safe-area-inset-top)] pl-[env(safe-area-inset-left)] pb-[env(safe-area-inset-bottom)]
          ${sidebarOpen ? 'w-64' : 'w-16'}
        `}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 dark:border-gray-800">
          {sidebarOpen && (
            <span className="text-xl font-bold text-primary-600">TaskFly</span>
          )}
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <ChevronLeft className={`w-5 h-5 text-gray-500 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
          {navigation
            .filter(item => !item.adminOnly || isAdmin)
            .map((item) => {
              const badgeCount = getBadgeCount(item.name, item.showBadge);
              return (
                <NavLink
                  key={item.name}
                  to={item.path}
                  className={({ isActive }) =>
                    `relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors min-h-[44px] ${
                      isActive
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
                    }`
                  }
                >
                  <div className="relative">
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    <NotificationDot
                      visible={badgeCount > 0}
                      ariaLabel={t('layout.badge.newItems', { count: badgeCount, name: t(`layout.nav.${navKeys[item.name]}`) })}
                    />
                  </div>
                  {sidebarOpen && (
                    <>
                      <span className="font-medium flex-1">{t(`layout.nav.${navKeys[item.name]}`)}</span>
                      <span className={`text-xs bg-red-500 text-white rounded-full px-2 py-0.5 min-w-[20px] text-center transition-all duration-300 ${badgeCount > 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'}`}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    </>
                  )}
                </NavLink>
              );
            })}
        </nav>

        {/* Dark mode toggle for desktop (md+) - xs uses mobile sidebar, sm uses header toggle */}
        <div className="px-2 py-2 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={toggleDarkMode}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors min-h-[44px] min-w-[44px] w-full ${!sidebarOpen ? 'justify-center' : ''}`}
            aria-label={darkMode ? t('layout.switchToLightMode') : t('layout.switchToDarkMode')}
          >
            {darkMode ? (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
            {sidebarOpen && (
              <span className="font-medium">{darkMode ? t('layout.theme.lightMode') : t('layout.theme.darkMode')}</span>
            )}
          </button>
        </div>

        {/* Quick actions - New Task always visible, New Project only in Projects page */}
        {sidebarOpen && (
          <div className="px-2 py-2 border-t border-gray-200 dark:border-gray-800 space-y-1">
            <Button
              variant="primary"
              className="w-full justify-start min-h-[44px]"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => openTaskModal()}
            >{t('common.newTask')}</Button>
            {isProjectsPage && (
              <Button
                variant="secondary"
                className="w-full justify-start min-h-[44px]"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => openProjectModal()}
              >{t('common.newProject')}</Button>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default Sidebar;